import { Observable } from 'rxjs'
import { map } from 'rxjs/operators'
import { ofType, StateObservable } from 'redux-observable'
import {
  AssetType,
  CallableType,
  CollateralBasketType,
  Market,
  PreparedOrderClearingDetail,
  RepoClassification,
  SettlementVenue,
  TradingEntityClearingDetail,
  UserPreference
} from 'api'
import { GlobalState } from 'store'
import { SelectionActions, SelectionActionTypes } from 'store/selection'
import { selectionStoreSelector } from 'selectors/storeSelectors'
import { createUserPreferenceSelector } from 'selectors/userPreferenceSelectors'
import { createPreparedStagingOrders } from 'store/selection/negotiationInstructionBuilder'
import { getCommonClearingDetails, getSettlementVenueOptions } from 'selectors/clearingRelationshipsBuilder'
import { tradingEntityClearingDetailsSelector } from 'selectors/clearingSelectors'
import { PreparedStagingOrder } from 'store/selection/selectionTypes'
import { orderDetailIsSBL, tradingEntityClearingTypeFromSettlementVenue, wasUploadedFromSpreadsheet } from 'model'
import { BTQAction } from 'epics'
import { Nullable } from 'utils'
import { isNil } from 'lodash'

type PrepareStagingOrdersAction = ReturnType<typeof SelectionActions.prepareStagingOrders>
type UnprepareStagingOrdersAction = ReturnType<typeof SelectionActions.unprepareStagingOrders>

export function orderPrepareEpic(action$: Observable<BTQAction>, state$: StateObservable<GlobalState>): Observable<BTQAction> {
  const userPreferenceSelector = createUserPreferenceSelector()

  return action$.pipe(
    ofType<BTQAction, SelectionActionTypes.PREPARE_STAGING_ORDERS, PrepareStagingOrdersAction>(SelectionActionTypes.PREPARE_STAGING_ORDERS),
    map(action => {
      const state = state$.value
      const currentPreparedStagingOrders = selectCurrentPreparedStagingOrders(state, action.payload.windowId)
      const currentPreparedStagingOrderIds = currentPreparedStagingOrders.map(
        preparedStagingOrder => preparedStagingOrder.orderDetail.order.orderId
      )

      const newPreparedStagingOrders: PreparedStagingOrder[] = action.payload.orderDetails
        .filter(orderDetail => !currentPreparedStagingOrderIds.includes(orderDetail.order.orderId))
        .map(orderDetail => ({
          orderDetail: orderDetail,
          negotiationInstruction: orderDetail.order.negotiationInstruction,
          clearingDetails: [],
          willOverwrite: false
        }))

      const mergedPreparedStagingOrders = getNewPreparedStagingOrders(
        currentPreparedStagingOrders,
        newPreparedStagingOrders,
        userPreferenceSelector(state),
        tradingEntityClearingDetailsSelector(state)
      )

      return SelectionActions.setPreparedStagingOrders(mergedPreparedStagingOrders, action.payload.windowId)
    })
  )
}

export function orderUnprepareEpic(action$: Observable<BTQAction>, state$: StateObservable<GlobalState>): Observable<BTQAction> {
  const userPreferenceSelector = createUserPreferenceSelector()

  return action$.pipe(
    ofType<BTQAction, SelectionActionTypes.UNPREPARE_STAGING_ORDERS, UnprepareStagingOrdersAction>(
      SelectionActionTypes.UNPREPARE_STAGING_ORDERS
    ),
    map(action => {
      const state = state$.value
      const currentPreparedStagingOrders = selectCurrentPreparedStagingOrders(state, action.payload.windowId)

      const newPreparedStagingOrders = currentPreparedStagingOrders.filter(
        preparedStagingOrder => !action.payload.orderIds.includes(preparedStagingOrder.orderDetail.order.orderId)
      )

      const mergedPreparedStagingOrders = getNewPreparedStagingOrders(
        [],
        newPreparedStagingOrders,
        userPreferenceSelector(state),
        tradingEntityClearingDetailsSelector(state)
      )

      return SelectionActions.setPreparedStagingOrders(mergedPreparedStagingOrders, action.payload.windowId)
    })
  )
}

function selectCurrentPreparedStagingOrders(state: GlobalState, windowId: string): PreparedStagingOrder[] {
  const windowSelections = selectionStoreSelector(state).windows[windowId]
  return windowSelections ? windowSelections.preparedStagingOrders : []
}

function getNewPreparedStagingOrders(
  currentPreparedStagingOrders: PreparedStagingOrder[],
  newPreparedStagingOrders: PreparedStagingOrder[],
  userPreference: UserPreference,
  tradingEntityClearingDetails: TradingEntityClearingDetail[]
) {
  let mergedPreparedStagingOrders = createPreparedStagingOrders(currentPreparedStagingOrders, newPreparedStagingOrders, userPreference)
  return addClearingDetailsToStagingOrders(mergedPreparedStagingOrders, tradingEntityClearingDetails, userPreference)
}

function addClearingDetailsToStagingOrders(
  stagingOrders: PreparedStagingOrder[],
  tradingEntityClearingDetails: TradingEntityClearingDetail[],
  userPreference: UserPreference
): PreparedStagingOrder[] {
  const orderDetails = stagingOrders.map(stagingOrder => stagingOrder.orderDetail)

  const clearingDetailsMap = getCommonClearingDetails(orderDetails, tradingEntityClearingDetails)
  const settlementVenue = getSettlementVenue(stagingOrders)

  const isBasket = !!orderDetails[0]?.order.basketAllocation
  const basketType = (orderDetails.length > 0 && orderDetails[0].productSector?.basketType) || null
  const someOrdersAreBlockGcBespoke = orderDetails.some(
    orderDetail => orderDetail.order.blockTrade && orderDetail.productSector?.basketType === CollateralBasketType.GC_BESPOKE
  )
  const someOrdersAreOpenRepo = orderDetails.some(
    orderDetail =>
      orderDetail.order.repoClassification === RepoClassification.OPEN ||
      orderDetail.order.repoClassification === RepoClassification.OPEN_EVERGREEN
  )
  const someOrdersAreCallable = orderDetails.some(orderDetail => orderDetail.order.callableType !== CallableType.NONE)
  const allOrdersAreUs = orderDetails.every(orderDetail => orderDetail.order.market === Market.US)
  const orderIsStockBorrowingLending = isBasket && orderDetails.some(orderDetailIsSBL)
  const orderIsGCPlus = orderDetails.some(orderDetail => orderDetail.productSector?.basketType === CollateralBasketType.GC_PLUS)
  const someOrdersAreCorp = orderDetails.some(orderDetail => orderDetail.collateral?.assetType === AssetType.CORP)

  // If all orders have compatible negotiation instructions and have been prepared at least once
  // then we do not force the user preference default settlement
  const suppressSettlementVenueUserPreference =
    (stagingOrders.every(stagingOrder => !stagingOrder.willOverwrite) &&
      (stagingOrders.length === 0 || stagingOrders[0].negotiationInstruction.counterpartyInstructions.length > 0)) ||
    stagingOrders.some(({ orderDetail }) => wasUploadedFromSpreadsheet(orderDetail.order))

  const compatibleSettlementVenue = getCompatibleSettlementVenue(
    settlementVenue,
    clearingDetailsMap,
    basketType,
    someOrdersAreBlockGcBespoke,
    someOrdersAreOpenRepo,
    someOrdersAreCallable,
    allOrdersAreUs,
    suppressSettlementVenueUserPreference,
    userPreference,
    orderIsStockBorrowingLending,
    orderIsGCPlus,
    someOrdersAreCorp
  )
  const compatibleClearingType = tradingEntityClearingTypeFromSettlementVenue(compatibleSettlementVenue)

  const settlementVenueCompatible = settlementVenue === compatibleSettlementVenue

  return stagingOrders.map(stagingOrder => ({
    ...stagingOrder,
    negotiationInstruction: {
      ...stagingOrder.negotiationInstruction,
      settlementVenue: compatibleSettlementVenue,
      counterpartyInstructions: settlementVenueCompatible ? stagingOrder.negotiationInstruction.counterpartyInstructions : [],
      clearingType: compatibleClearingType
    },
    clearingDetails: clearingDetailsMap[stagingOrder.orderDetail.order.orderId]
      ? clearingDetailsMap[stagingOrder.orderDetail.order.orderId]
      : [],
    willOverwrite: settlementVenueCompatible ? stagingOrder.willOverwrite : true
  }))
}

function getSettlementVenue(stagingOrders: PreparedStagingOrder[]) {
  return stagingOrders.length > 0 ? stagingOrders[0].negotiationInstruction.settlementVenue : SettlementVenue.Bilateral
}

function getCompatibleSettlementVenue(
  settlementVenue: Nullable<SettlementVenue>,
  clearingDetailsMap: Record<string, PreparedOrderClearingDetail[]>,
  basketType: CollateralBasketType | null,
  someOrdersAreBlockGcBespoke: boolean,
  someOrdersAreOpenRepo: boolean,
  someOrdersAreCallable: boolean,
  allOrdersAreUs: boolean,
  suppressSettlementVenueUserPreference: boolean,
  userPreference: UserPreference,
  orderIsStockBorrowingLending: boolean,
  orderIsGCPlus: boolean,
  someOrdersAreCorp: boolean
): Nullable<SettlementVenue> {
  if (isNil(settlementVenue)) {
    return null
  }

  if (basketType === CollateralBasketType.EUROCLEAR || basketType === CollateralBasketType.BONY) {
    return SettlementVenue.Triparty
  }

  if (Object.values(clearingDetailsMap).length === 0 && !allOrdersAreUs) {
    return SettlementVenue.Bilateral
  }
  const firstOrderClearingDetails = Object.values(clearingDetailsMap)[0] ?? []
  const availableSettlementVenues = getSettlementVenueOptions(
    firstOrderClearingDetails,
    basketType,
    someOrdersAreOpenRepo,
    someOrdersAreBlockGcBespoke,
    someOrdersAreCallable,
    allOrdersAreUs,
    orderIsStockBorrowingLending,
    orderIsGCPlus,
    someOrdersAreCorp
  )

  if (!availableSettlementVenues.includes(settlementVenue)) {
    return SettlementVenue.Bilateral
  }

  if (suppressSettlementVenueUserPreference) {
    return settlementVenue
  }

  return !availableSettlementVenues.includes(userPreference.settlementVenueDefault)
    ? settlementVenue
    : userPreference.settlementVenueDefault
}
